<template>
  <div class="p-6">
    <div class="space-y-6">
      <!-- Chart -->
      <div class="relative h-40">
        <Pie
          :data="chartData"
          :options="chartOptions"
          :chart-id="chartId"
          :dataset-id-key="datasetIdKey"
          :plugins="plugins"
          :css-classes="cssClasses"
          :styles="styles"
          :width="width"
          :height="height"
        />
      </div>

      <!-- Summary Stats -->
      <div class="space-y-4">
        <div class="flex justify-between items-center py-3 border-b border-gray-100">
          <div class="flex items-center space-x-2">
            <div class="w-3 h-3 bg-blue-500 rounded-full" />

            <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.CashFlow.workDone') }}</span>
          </div>

          <span class="text-sm font-semibold text-blue-600">{{ $tcc(workDone) }}</span>
        </div>

        <div class="flex justify-between items-center py-3 border-b border-gray-100">
          <div class="flex items-center space-x-2">
            <div class="w-3 h-3 bg-red-500 rounded-full" />

            <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.CashFlow.remaining') }}</span>
          </div>

          <span class="text-sm font-semibold text-red-600">{{ $tcc(remaining) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import { t } from '@/i18n';
import { ArcElement, Chart as ChartJS, Legend, Title, Tooltip } from 'chart.js';
import { computed } from 'vue';
import { Pie } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Legend,
  Title,
  Tooltip,
);

interface Props {
  cumulativeAmounts: number[];
  totalBudget: number;
  hoveredIndex?: number;
}

const props = defineProps<Props>();

// Computed properties for template
const workDone = computed(() => {
  const index = props.hoveredIndex !== undefined ? props.hoveredIndex : (props.cumulativeAmounts.length - 1);

  return props.cumulativeAmounts[index] || 0;
});

const remaining = computed(() => {
  return Math.max(props.totalBudget - workDone.value, 0);
});

// Chart configuration
const chartId = 'cashflow-pie-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const chartData = computed(() => ({
  labels: [t('HeadContract.CashFlow.workDone'), t('HeadContract.CashFlow.remaining')],
  datasets: [{
    data: [workDone.value, remaining.value],
    backgroundColor: [
      'rgba(59, 130, 246, 0.8)',
      'rgba(239, 68, 68, 0.8)',
    ],

    borderColor: [
      'rgba(59, 130, 246, 1)',
      'rgba(239, 68, 68, 1)',
    ],

    borderWidth: 2,
    hoverOffset: 4,
  }],
}));

const chartOptions = computed(() => ({
  responsive: true,
  plugins: {
    legend: {
      display: false, // We have custom legend in the card
    },

    title: {
      display: false,
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      callbacks: {
        label(this: any, tooltipItem: any) {
          const value = tooltipItem.dataset.data[tooltipItem.dataIndex];
          const percentage = props.totalBudget > 0 ? (value / props.totalBudget * 100).toFixed(1) : '0';

          return `${tooltipItem.label}: ${australianCurrency(value)} (${percentage}%)`;
        },
      },
    },
  },

  maintainAspectRatio: false,
}));
</script>

<style scoped></style>
